import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiHandler from '../services/apiServices';
import { Organization } from '../types/models';
import { useNavigate } from '@tanstack/react-router';
import axios from 'axios';
import { useSupabase } from '../contexts/SupabaseContext';

export const useOrganization = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session } = useSupabase();

  // Fetch organization details
  const { data: organization, isLoading, error } = useQuery<Organization>({
    queryKey: ['organization'],
    queryFn: async () => {
      try {
        const response = await apiHandler.get('/org');
        return response.data;
      } catch (error) {
        throw error;
      }
    },
    retry: false,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
    enabled: !!session, // Only run query if session exists
  });

  // Update organization details
  const updateOrganization = useMutation({
    mutationFn: async (updatedOrg: Partial<Organization>) => {
      // Only make the API call if there are actual changes
      const currentData = queryClient.getQueryData<Organization>(['organization']);
      const hasChanges = Object.keys(updatedOrg).some(
        key => updatedOrg[key as keyof Organization] !== currentData?.[key as keyof Organization]
      );

      if (!hasChanges) {
        return currentData;
      }

      const response = await apiHandler.patch('/org', updatedOrg);
      return response.data;
    },
    onSuccess: (newOrg) => {
      queryClient.setQueryData(['organization'], newOrg);
    },
  });

  // Create organization
  const createOrganization = useMutation({
    mutationFn: async (newOrg: Organization) => {
      const response = await apiHandler.post('/', newOrg);
      return response.data;
    },
    onSuccess: (newOrg) => {
      queryClient.setQueryData(['organization'], newOrg);
    },
  });

  // Update LinkedIn URL
  const updateLinkedInUrl = useMutation({
    mutationFn: async (linkedinUrl: string) => {
      const response = await apiHandler.put('/org', {
        linkedin_url: linkedinUrl
      });
      return response.data;
    },
    onSuccess: (newOrg) => {
      queryClient.setQueryData(['organization'], newOrg);
    },
  });

  return {
    organization,
    isLoading,
    error,
    updateOrganization: updateOrganization.mutateAsync,
    createOrganization: createOrganization.mutate,
    isUpdating: updateOrganization.isPending,
    isCreating: createOrganization.isPending,
    updateLinkedInUrl: updateLinkedInUrl.mutate,
  };
}; 